import { native } from '@forest/native-services'
import { createSignal, Match, onMount, Show, Switch } from 'solid-js'
import { CheckCutoutIcon } from '~/components/icons/check-cutout'
import { ChevronRightIcon } from '~/components/icons/chevron-right'
import { ClockIcon } from '~/components/icons/clock'
import { ProgressiveBlur } from '~/components/progressive-blur'
import { SpecialProgressBar } from '~/components/special-progress-bar'
import { Spinner } from '~/components/spinner'
import { createCountdown } from '~/lib/countdown'
import { createScrollDirectionVisibility } from '~/lib/scroll-visibility'
import { specialTimerText } from '~/lib/special-challenge-timer'
import { cn } from '~/lib/utils'
import { m } from '~/paraglide/messages'
import type {
  SpecialChallengeConfig,
  VisibleSpecialChallengeCard,
} from '~/resources/special-challenge'

type Props = {
  card: VisibleSpecialChallengeCard
  config: SpecialChallengeConfig
  claiming: boolean
  onClaim: (event: MouseEvent) => void
  onOpenDetail: () => void
}

export function SpecialChallengeCard(props: Props) {
  // Config carries bare colors; the card owns direction and stops. The bg
  // sweeps left-to-right, the inside stroke top-to-bottom, per the Figma.
  const cardGradient = () => {
    const [from, to] = props.config.entry_bg_gradient_from_to
    return `linear-gradient(90deg, ${from} 0%, ${to} 100%)`
  }
  // Figma paints the card's 3px inside stroke as a semi-transparent vertical
  // gradient composited OVER the card gradient. CSS borders can't take a
  // gradient, so the card stacks three background layers instead: the card
  // gradient clipped to the padding box (covering the inner area), then the
  // stroke gradient over the card gradient again where only the border ring
  // shows through.
  const borderGradient = () => {
    const [from, to] = props.config.entry_border_gradient_from_to
    return `linear-gradient(180deg, ${from} 0%, ${to} 100%)`
  }
  const countdown = createCountdown(() => props.card.countdownTo)
  const timerText = () =>
    specialTimerText(props.card.kind, countdown().totalSeconds)
  const barCard = () => (props.card.kind === 'upcoming' ? null : props.card)

  const scrollVisible = createScrollDirectionVisibility()

  // Exposure counts every hidden→visible transition (any visible state —
  // upcoming, active, grace); the scroll-away slide below only translates
  // the mounted card, so it never re-fires.
  onMount(() => {
    void native.general.logEvent({
      event_name: 'component_show',
      event_params: { component_name: 'special_challenge_banner' },
    })
  })

  // The blur backdrop fades in only after the banner has finished entering
  // (initial mount or the 300ms slide back in) and cuts immediately when it
  // hides. `entered` flips a couple of frames after mount so the opacity-0
  // starting state actually paints before the transition runs.
  const [entered, setEntered] = createSignal(false)
  onMount(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)))
  })
  const blurShown = () => entered() && scrollVisible()
  // Applied to every blur band and the tint individually so the whole
  // backdrop fades as one uniform piece — see the layerClass note in
  // ProgressiveBlur for why the wrapper itself must not carry the opacity.
  const blurFade = () =>
    cn(
      'transition-opacity duration-500',
      blurShown() ? 'opacity-100 delay-300' : 'opacity-0 delay-0',
    )

  return (
    // Fixed bottom banner floating over the hub. The wrapper ignores pointer
    // events so the pb-12 gap doesn't swallow taps meant for the content
    // behind it; the card re-enables them for the Claim button. Scrolling
    // down slides it fully off-screen, scrolling up brings it back.
    <section
      class={cn(
        'pointer-events-none fixed inset-x-0 bottom-0 z-20 px-[12px] pb-12 transition-transform duration-300 ease-out',
        // The blur backdrop below extends above the section's own height, so
        // a 100% translate would leave its top edge peeking into the
        // viewport — slide by a fixed distance that clears the whole stack.
        !scrollVisible() && 'translate-y-[240px]',
      )}
    >
      <div class="absolute inset-x-0 bottom-0 h-[220px]">
        <ProgressiveBlur strong="bottom" maxBlur={30} layerClass={blurFade()} />
        <div
          class={cn(
            'absolute inset-0 bg-gradient-to-b from-surface-mint/0 to-surface-mint',
            blurFade(),
          )}
        />
      </div>
      <div
        class="pointer-events-auto relative mx-auto flex w-full max-w-[640px] cursor-pointer items-center gap-[12px] overflow-hidden rounded-[32px] py-[18px] pl-[14px] pr-[18px]"
        style={{
          border: '3px solid transparent',
          background: `${cardGradient()} padding-box, ${borderGradient()} border-box, ${cardGradient()} border-box`,
        }}
        data-kind={props.card.kind}
        data-bar={barCard()?.bar}
        onClick={() => props.onOpenDetail()}
      >
        <img
          src={props.config.reward_sticker_img_url}
          alt=""
          draggable={false}
          class="pointer-events-none h-[99px] w-[98px] shrink-0 object-cover opacity-90"
        />

        <div class="flex min-w-0 flex-1 flex-col items-start gap-[8px]">
          <div class="flex h-[22px] w-full items-center gap-[4px]">
            <p class="min-w-0 max-w-[200px] truncate text-[16px] font-semibold leading-[22px] text-black/70">
              {m.special_challenge_banner_title()}
            </p>
            <ChevronRightIcon class="h-[14px] w-[14px] shrink-0 text-black/70" />
          </div>

          <Switch>
            <Match when={barCard()?.bar === 'progress' && barCard()}>
              {(b) => (
                <SpecialProgressBar
                  progress={b().progress}
                  target={b().target}
                  trackColor={props.config.progress_bar_bg_color}
                  fillBg={props.config.progress_bg}
                />
              )}
            </Match>

            <Match when={barCard()?.bar === 'claim'}>
              <button
                type="button"
                disabled={props.claiming}
                onClick={(e) => {
                  // The whole card opens the detail view; claiming stays an
                  // in-place action on the card.
                  e.stopPropagation()
                  // Pure intent: counts the tap, not the claim outcome.
                  void native.general.logEvent({
                    event_name: 'component_event',
                    event_params: {
                      type: 'btn',
                      app_action: 'click',
                      component_name: 'special_challenge_claim',
                    },
                  })
                  props.onClaim(e)
                }}
                class="tap-highlight-none relative flex h-[40px] w-full items-center justify-center overflow-hidden rounded-[8px] text-sm font-semibold leading-[20px] text-white transition-transform duration-200 active:scale-[0.98]"
                style={{ background: props.config.claim_reward_btn_bg }}
              >
                <span
                  aria-hidden="true"
                  class="pointer-events-none absolute inset-0 overflow-hidden rounded-[8px]"
                >
                  <span class="absolute -bottom-[60%] -left-[40%] -top-[60%] flex h-[220%] w-[60%] animate-shimmer items-center justify-center">
                    <span class="h-full w-[28%] rotate-[31deg] skew-x-[3deg] bg-gradient-to-b from-white/0 via-white/55 to-white opacity-90" />
                    <span class="ml-[10%] h-full w-[12%] rotate-[31deg] skew-x-[3deg] bg-gradient-to-b from-white/0 via-white/45 to-white opacity-80" />
                  </span>
                </span>
                <Show
                  when={!props.claiming}
                  fallback={
                    <Spinner
                      variant="circle"
                      class="relative size-[20px] text-white"
                    />
                  }
                >
                  <span class="relative">{m.monthlytask_claim()}</span>
                </Show>
              </button>
            </Match>

            <Match when={barCard()?.bar === 'complete'}>
              <div
                class="flex h-[40px] w-full items-center justify-center gap-[4px] rounded-[8px] opacity-40"
                style={{ background: props.config.claim_reward_btn_bg }}
              >
                <span class="text-sm font-semibold leading-[20px] text-white">
                  {m.monthlytask_claimed()}
                </span>
                <CheckCutoutIcon class="size-3" />
              </div>
            </Match>
          </Switch>

          <div
            class={`flex items-center gap-[2px] text-[12px] leading-[16px] ${
              props.card.kind === 'upcoming' ? 'text-black/50' : 'text-black/60'
            }`}
          >
            <ClockIcon class="size-[16px] shrink-0" />
            <span class="whitespace-nowrap">{timerText()}</span>
          </div>
        </div>

        <Show when={barCard()?.bar !== 'complete'}>
          <div
            aria-hidden="true"
            class="pointer-events-none absolute inset-0 overflow-hidden rounded-[32px]"
          >
            <div class="absolute -left-[110px] top-1/2 h-[251px] w-[99px] animate-special-sweep bg-white opacity-30 shadow-[0px_0px_15px_0px_rgba(255,255,255,0.5)]" />
          </div>
        </Show>
      </div>
    </section>
  )
}
